import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import {
  AccountTreeOutlined,
  AnalyticsOutlined,
  AssignmentOutlined,
  AutoFixHighOutlined,
  BarChartOutlined,
  BoltOutlined,
  BugReportOutlined,
  ChatOutlined,
  ChecklistOutlined,
  CheckCircleOutlined,
  CodeOutlined,
  DescriptionOutlined,
  ExtensionOutlined,
  GitHub,
  GroupsOutlined,
  LibraryBooksOutlined,
  Login as LoginIcon,
  MonitorHeartOutlined,
  PersonSearchOutlined,
  PsychologyOutlined,
  QueueOutlined,
  SyncAltOutlined,
  TerminalOutlined,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';

interface FeatureCard {
  icon: SvgIconComponent;
  title: string;
  description: string;
  mock: string;
}

interface RequirementCard {
  title: string;
  detail: string;
  type: 'required' | 'optional';
  unlocks: string;
}

const agenticCards: FeatureCard[] = [
  {
    icon: ChatOutlined,
    title: 'Agentic Chat Commands',
    description:
      '`!agent ask` for instant answers, `!agent task` for autonomous work — all from Google Chat.',
    mock: '> !agent task fix the null pointer in OrderService\n⚙ Analysing... branch created\n✓ PR #42 ready for review',
  },
  {
    icon: TerminalOutlined,
    title: 'Interactive Agent Sessions',
    description:
      '`!agent interactive` opens a persistent Claude Code session in a GChat thread. Continue multi-turn conversations across messages with a live streaming console.',
    mock: '> !agent interactive\n⚡ Session started · console: /sessions/a1b2\n\n> !agent stop\n✓ Session ended · 3 turns · $0.42',
  },
  {
    icon: AssignmentOutlined,
    title: 'Jira Spy & Auto-Assignment',
    description:
      'Watches for `ai_wingman`-labelled tickets, picks them up, implements the fix, and shares the PR.',
    mock: 'TICKET-99 labelled ai_wingman\n→ branch: fix/ticket-99-npe\n→ PR opened, reviewer notified',
  },
  {
    icon: AutoFixHighOutlined,
    title: 'AI Refinement in Jira Kanban',
    description:
      'Refines descriptions, adds acceptance criteria, and estimates story points directly in the board.',
    mock: 'TICKET-12: vague description\n→ AC added (5 items)\n→ estimate: 3 sp',
  },
  {
    icon: DescriptionOutlined,
    title: 'Spec-Driven Development',
    description: 'Feed an OpenAPI spec or PRD and receive a working implementation with tests.',
    mock: 'openapi.yaml → src/\n  routes/users.ts\n  models/User.ts\n  tests/users.spec.ts',
  },
  {
    icon: ExtensionOutlined,
    title: 'Skills / Plugin Catalogue',
    description:
      'Admin-managed library of AI skills loaded on demand — extend the agent without code changes.',
    mock: 'skills/\n  code-review.md\n  security-audit.md\n  perf-profile.md',
  },
  {
    icon: AccountTreeOutlined,
    title: 'AI Workflows',
    description: 'Cron or event-triggered multi-step agent pipelines for repeatable tasks.',
    mock: 'cron: 0 9 * * 1\n→ weekly-report workflow\n  step 1/3 ✓ step 2/3 ✓',
  },
  {
    icon: ChecklistOutlined,
    title: 'AI Todos',
    description:
      'Session-spanning task list that persists across agent runs so nothing falls through the cracks.',
    mock: '[ ] write migration\n[x] add endpoint\n[ ] update docs',
  },
  {
    icon: PsychologyOutlined,
    title: 'Self-Learning Memory',
    description: 'Learns from corrections and avoids repeating mistakes across conversations.',
    mock: 'memory: avoid snake_case\n  in Python DTOs\n  (corrected 2026-08-01)',
  },
  {
    icon: QueueOutlined,
    title: 'Task Queue & Sessions',
    description: 'Real-time view of running and queued agent tasks with live output streaming.',
    mock: 'queue (2 running)\n  → TICKET-99 [2m 14s]\n  → weekly-report [48s]',
  },
  {
    icon: MonitorHeartOutlined,
    title: 'Agent Config & Healthcheck',
    description: 'Per-team Claude Code agent configuration with a live healthcheck endpoint.',
    mock: 'agent: claude-sonnet-4\nstatus: healthy ✓\nlast ping: 3s ago',
  },
  {
    icon: SyncAltOutlined,
    title: 'Model Failover / Qwen3',
    description:
      'Auto-switches between Anthropic Claude and IONOS Qwen3 when one provider is unavailable.',
    mock: 'primary: claude ✓\nfallback: qwen3\n  (triggered 0 times today)',
  },
  {
    icon: BarChartOutlined,
    title: 'Usage & Cost Dashboard',
    description: 'Token usage, cost per task, and per-team breakdown for budget visibility.',
    mock: 'this week: 1.2M tokens\ncost: $4.80\ntop team: Platform (38%)',
  },
];

const platformCards: FeatureCard[] = [
  {
    icon: PersonSearchOutlined,
    title: 'Expert Routing',
    description:
      'Routes questions to the right team member based on topic and defined expertise areas.',
    mock: 'Q: Kubernetes config?\n→ matched: @alex (k8s expert)\n   notified in thread',
  },
  {
    icon: BoltOutlined,
    title: 'Response Caching',
    description: 'Caches frequent answers to cut latency and token costs for repeated questions.',
    mock: 'cache hit rate: 34%\nsaved: 210K tokens\nthis week',
  },
  {
    icon: AnalyticsOutlined,
    title: 'Analytics Dashboard',
    description: 'Activity feed, response stats, escalation rates, and top questions at a glance.',
    mock: 'responses: 142 today\nescalations: 3\ntop topic: CI failures',
  },
  {
    icon: GroupsOutlined,
    title: 'Multi-team Support',
    description:
      'Isolated team spaces with separate configs, experts, knowledge bases, and AI providers.',
    mock: 'teams: 8 active\n  Platform (Claude)\n  Mobile (Qwen3)',
  },
];

const integrationCards: FeatureCard[] = [
  {
    icon: BugReportOutlined,
    title: 'Jira Auto-Creation & Sync',
    description:
      'Creates Jira tickets from chat, syncs GitHub PR status back to Jira automatically.',
    mock: 'chat → TICKET-88 created\nPR #42 merged\n→ TICKET-88: Done ✓',
  },
  {
    icon: GitHub,
    title: 'GitHub Issues View',
    description: 'Unified view of open issues across all team repositories inside the app.',
    mock: 'repos: 12 | open: 47\n  platform-api: 12\n  mobile-sdk: 8',
  },
  {
    icon: LibraryBooksOutlined,
    title: 'Confluence Integration',
    description:
      'Pulls pages from Confluence spaces into the knowledge base for AI-powered answers.',
    mock: 'synced: 34 pages\nlast update: 10m ago\nspace: PLATFORM',
  },
  {
    icon: CodeOutlined,
    title: 'GitLab Integration',
    description:
      'Mirrors GitHub support for GitLab: issues view, PR sharing, and per-team tokens.',
    mock: 'gitlab.ionos.com\nprojects: 6 linked\nMR #18 → TICKET-77',
  },
];

const requirements: RequirementCard[] = [
  {
    title: 'Google Chat Space',
    detail: 'Space ID (spaces/AAAA…) · webhook URL in Cloud Console',
    type: 'required',
    unlocks: 'Core bot functionality',
  },
  {
    title: 'AI Provider Token',
    detail: 'Anthropic API key (sk-ant-…) OR IONOS AI token for Qwen3/Llama',
    type: 'required',
    unlocks: 'All AI responses',
  },
  {
    title: 'Jira Integration',
    detail: 'Base URL · username · API token · project key',
    type: 'optional',
    unlocks: 'Ticket creation, Jira spy, AI refinement',
  },
  {
    title: 'GitHub / GitLab PAT',
    detail: 'repo + issues scope',
    type: 'optional',
    unlocks: 'Issues view, PR sharing, spec-driven dev, sync',
  },
  {
    title: 'Confluence',
    detail: 'Base URL · API token · space key',
    type: 'optional',
    unlocks: 'Confluence knowledge base sync',
  },
  {
    title: 'Claude Code CLI',
    detail: 'Installed on agent host · Anthropic API key',
    type: 'optional',
    unlocks: 'All !agent features, AI todos, Jira spy',
  },
];

function SectionHeader({
  label,
  title,
  subtitle,
}: {
  label: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <Box sx={{ textAlign: 'center', mb: 6 }}>
      <Typography
        variant="overline"
        sx={{ color: '#5c9cf5', letterSpacing: 3, fontWeight: 700, fontSize: '0.7rem' }}
      >
        {label}
      </Typography>
      <Typography
        variant="h3"
        sx={{ color: '#ffffff', fontWeight: 800, mt: 1, mb: subtitle ? 1 : 0 }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body1" sx={{ color: '#8899aa', maxWidth: 600, mx: 'auto' }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

function MockOutput({ text }: { text: string }) {
  return (
    <Box
      component="pre"
      sx={{
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#5c7a8a',
        background: '#0f0f1a',
        border: '1px solid #1e2a3a',
        borderRadius: 2,
        p: 1.5,
        mt: 2,
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        lineHeight: 1.6,
      }}
    >
      {text}
    </Box>
  );
}

function FeatureCardItem({ card }: { card: FeatureCard }) {
  const Icon = card.icon;
  return (
    <Box
      sx={{
        background: '#1a1a2e',
        border: '1px solid #2a2a4a',
        borderRadius: 3,
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color 0.2s',
        '&:hover': { borderColor: '#5c9cf5' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Icon sx={{ color: '#5c9cf5', fontSize: 24 }} />
        <Typography variant="subtitle1" sx={{ color: '#e0e8f0', fontWeight: 700 }}>
          {card.title}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: '#8899aa', lineHeight: 1.6, flex: 1 }}>
        {card.description}
      </Typography>
      <MockOutput text={card.mock} />
    </Box>
  );
}

export default function WelcomePage() {
  const navigate = useNavigate();

  const handleScrollToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box sx={{ background: '#0a0a0a', minHeight: '100vh', color: '#ffffff' }}>
      {/* Sticky nav */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: 'rgba(10,10,10,0.95)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid #1a1a2e',
          px: 4,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#ffffff' }}>
          AI Wingman
        </Typography>
        <Button
          variant="contained"
          startIcon={<LoginIcon />}
          onClick={() => navigate('/login')}
          size="small"
          sx={{ borderRadius: 2 }}
        >
          Sign In
        </Button>
      </Box>

      {/* Hero */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0d1b4b 0%, #1a237e 50%, #0a3d6b 100%)',
          py: { xs: 10, md: 14 },
          px: 2,
          textAlign: 'center',
        }}
      >
        <Chip
          label="Agentic Support Platform"
          sx={{
            mb: 3,
            background: 'rgba(92,156,245,0.15)',
            color: '#5c9cf5',
            border: '1px solid #5c9cf5',
            fontWeight: 600,
          }}
        />
        <Typography
          variant="h1"
          sx={{
            fontWeight: 900,
            fontSize: { xs: '2.4rem', md: '3.8rem' },
            color: '#ffffff',
            mb: 2,
            lineHeight: 1.1,
          }}
        >
          Meet your team&apos;s AI Wingman
        </Typography>
        <Typography
          variant="h5"
          sx={{
            color: '#c8d8ee',
            maxWidth: 740,
            mx: 'auto',
            mb: 1.5,
            fontWeight: 400,
            lineHeight: 1.5,
          }}
        >
          Wingman codes, ships, and reviews &mdash; autonomously, 24/7. No engineer babysitting
          needed.
        </Typography>
        <Typography variant="body1" sx={{ color: '#8899aa', maxWidth: 540, mx: 'auto', mb: 5 }}>
          Your team gets notified when a PR is ready to review. That&apos;s it.
        </Typography>

        <Box
          sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 8 }}
        >
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/login')}
            sx={{ borderRadius: 2, px: 4, fontWeight: 700 }}
          >
            Get Started &mdash; Sign In
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={handleScrollToFeatures}
            sx={{
              borderRadius: 2,
              px: 4,
              borderColor: '#5c9cf5',
              color: '#5c9cf5',
              fontWeight: 700,
              '&:hover': { borderColor: '#5c9cf5', background: 'rgba(92,156,245,0.08)' },
            }}
          >
            Explore Features &darr;
          </Button>
        </Box>

        {/* Stats */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: { xs: 4, md: 10 },
            flexWrap: 'wrap',
          }}
        >
          {[
            { value: '21', label: 'Features' },
            { value: '24/7', label: 'Codes autonomously' },
            { value: 'PR only', label: 'Engineers review' },
          ].map(({ value, label }) => (
            <Box key={label} sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ fontWeight: 900, color: '#5c9cf5' }}>
                {value}
              </Typography>
              <Typography variant="body2" sx={{ color: '#8899aa', mt: 0.5 }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Agentic Core */}
      <Box id="features" sx={{ background: '#0a0a0a', py: 10, px: 2 }}>
        <Container maxWidth="lg">
          <SectionHeader
            label="Agentic Core"
            title="Autonomous agents that actually do the work"
            subtitle="From chat command to merged PR — with no human in the loop."
          />
          <Grid container spacing={3}>
            {agenticCards.map((card) => (
              <Grid item xs={12} sm={6} md={4} key={card.title}>
                <FeatureCardItem card={card} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Platform Features */}
      <Box sx={{ background: '#0d0d16', py: 10, px: 2 }}>
        <Container maxWidth="lg">
          <SectionHeader label="Platform Features" title="Smart defaults for every team" />
          <Grid container spacing={3}>
            {platformCards.map((card) => (
              <Grid item xs={12} sm={6} md={3} key={card.title}>
                <FeatureCardItem card={card} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Integrations */}
      <Box sx={{ background: '#0a0a0a', py: 10, px: 2 }}>
        <Container maxWidth="lg">
          <SectionHeader label="Integrations" title="Connects to your existing stack" />
          <Grid container spacing={3}>
            {integrationCards.map((card) => (
              <Grid item xs={12} sm={6} md={3} key={card.title}>
                <FeatureCardItem card={card} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Requirements */}
      <Box sx={{ background: '#0d0d16', py: 10, px: 2 }}>
        <Container maxWidth="lg">
          <SectionHeader
            label="Setup"
            title="Minimum requirements to go live"
            subtitle="Everything marked Required must be ready before submitting for activation."
          />
          <Grid container spacing={3}>
            {requirements.map((req) => (
              <Grid item xs={12} sm={6} key={req.title}>
                <Box
                  sx={{
                    background: '#1a1a2e',
                    border: '1px solid #2a2a4a',
                    borderLeft: '4px solid',
                    borderLeftColor: req.type === 'required' ? '#5c9cf5' : '#4caf7a',
                    borderRadius: '0 12px 12px 0',
                    p: 3,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ color: '#e0e8f0', fontWeight: 700 }}>
                      {req.title}
                    </Typography>
                    <Chip
                      label={req.type === 'required' ? 'Required' : 'Optional'}
                      size="small"
                      sx={{
                        background:
                          req.type === 'required'
                            ? 'rgba(92,156,245,0.15)'
                            : 'rgba(76,175,122,0.15)',
                        color: req.type === 'required' ? '#5c9cf5' : '#4caf7a',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: '#8899aa', mb: 1 }}>
                    {req.detail}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircleOutlined sx={{ fontSize: 14, color: '#4caf7a' }} />
                    <Typography variant="caption" sx={{ color: '#6a8a7a' }}>
                      Unlocks: {req.unlocks}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Final CTA */}
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/login')}
              sx={{ borderRadius: 2, px: 5, py: 1.5, fontWeight: 700, fontSize: '1.1rem' }}
            >
              Ready? Sign In and Create Your Team
            </Button>
            <Typography variant="caption" sx={{ display: 'block', color: '#556677', mt: 2 }}>
              Self-service setup · ~2 minutes · No IT ticket needed
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Divider sx={{ borderColor: '#1a1a2e' }} />
      <Box sx={{ background: '#0a0a0a', py: 3, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: '#445566' }}>
          AI Wingman · IONOS Internal Tools · Powered by Claude + Qwen3
        </Typography>
      </Box>
    </Box>
  );
}
